/**
 * Phase 1.5c — Laguna admin (internal) console API helpers.
 *
 * All endpoints are gated behind the `laguna_admin_session` HttpOnly cookie
 * (distinct from team + proposal sessions).
 */
import axios from "axios";

import { API } from "./api";

const http = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
    timeout: 30000,
});

/* --- Phase A6 — admin email+password signin (replaces magic link) --- */
export async function adminSignin({ email, password, remember_me = false }) {
    const res = await http.post(
        "/admin/signin",
        { email, password, remember_me },
        { withCredentials: true },
    );
    return res.data;
}

// Phase 3e — invite-code-gated admin self-registration. Used by the
// founding admin (or anyone the founder shares the code with) to
// bootstrap their own account on a fresh deploy.
export async function adminSignup({ email, password, invite_code }) {
    const res = await http.post(
        "/admin/signup",
        { email, password, invite_code },
    );
    return res.data;
}

export async function adminSetPassword(newPassword) {
    const res = await http.post(
        "/admin/password/set",
        { new_password: newPassword },
        { withCredentials: true },
    );
    return res.data;
}

export async function adminMe() {
    const res = await http.get("/admin/me");
    return res.data;
}

export async function adminLogout() {
    const res = await http.post("/admin/logout");
    return res.data;
}

export async function adminListSubmissions(status) {
    const res = await http.get("/admin/submissions", {
        params: status && status !== "all" ? { status } : {},
    });
    return res.data;
}

export async function adminSubmissionDetail(applicationId) {
    const res = await http.get(`/admin/submissions/${applicationId}`);
    return res.data;
}

export async function adminTransitionStatus(applicationId, to_status, admin_notes) {
    const res = await http.post(
        `/admin/submissions/${applicationId}/status`,
        { to_status, admin_notes },
    );
    return res.data;
}

export async function adminWarmLeads() {
    const res = await http.get("/admin/warm-leads");
    return res.data;
}

export async function adminRequestOutreach(proposalId, note) {
    const res = await http.post(
        `/admin/warm-leads/${proposalId}/request-outreach`,
        { note },
    );
    return res.data;
}

export function adminSubmissionPdfUrl(applicationId) {
    return `${API}/admin/submissions/${applicationId}/pdf`;
}

export function adminSubmissionCensusXlsxUrl(applicationId) {
    return `${API}/admin/submissions/${applicationId}/census-xlsx`;
}

export async function adminBatchInvite(applicationId, employees) {
    // Phase 1.5d — batch-create employee invitations + flip status to
    // enrollment_open. `employees` is an array of
    // { first_name, last_name, email, age, classification?, locale? }.
    const res = await http.post(
        `/admin/submissions/${applicationId}/batch-invite`,
        { employees },
    );
    return res.data;
}

/* ---------- Phase 2e — Admin: employee enrollments under an employer ---- */
export async function adminListEmployeeApplications(applicationId) {
    const res = await http.get(
        `/admin/employer-applications/${applicationId}/employee-applications`,
    );
    return res.data;
}

export function adminEmployeeApplicationPdfUrl(applicationId) {
    return `${API}/admin/employee-applications/${applicationId}/pdf`;
}


/* ---------- Phase 2d — AI-assisted census upload ---------- */
export async function censusParse(applicationId, file) {
    // Backend signature: file via multipart form-data,
    // employer_application_id as a query-string param.
    const fd = new FormData();
    fd.append("file", file);
    const res = await http.post("/employer/census/parse", fd, {
        params: { employer_application_id: applicationId },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
    });
    return res.data;
}

export async function censusMap(uploadId, forceRefresh = false) {
    const res = await http.post(
        "/employer/census/map",
        { upload_id: uploadId, force_refresh: forceRefresh },
        { timeout: 60000 },
    );
    return res.data;
}

export async function censusValidate(uploadId, mapping, rememberMapping = true, rowOverrides = {}) {
    const res = await http.post("/employer/census/validate", {
        upload_id: uploadId,
        mapping,
        remember_mapping: rememberMapping,
        row_overrides: rowOverrides,
    });
    return res.data;
}

export async function censusCommit(uploadId, mapping, rows, rowOverrides = {}) {
    const res = await http.post(
        "/employer/census/commit",
        {
            upload_id: uploadId,
            mapping,
            rows,
            row_overrides: rowOverrides,
        },
        { timeout: 60000 },
    );
    return res.data;
}

export async function censusUndoCommit(uploadId) {
    const res = await http.post(
        "/employer/census/undo_commit",
        { upload_id: uploadId },
        { timeout: 30000 },
    );
    return res.data;
}

export async function censusPasteCommit(applicationId, rows) {
    // Phase 2e §4 — paste now goes through the census pipeline so it
    // inherits the same 30s undo window as the upload tab.
    const res = await http.post(
        "/employer/census/paste_commit",
        {
            employer_application_id: applicationId,
            rows,
        },
        { timeout: 60000 },
    );
    return res.data;
}

export function censusSampleCsvUrl() {
    return `${API}/employer/census/sample.csv`;
}

export function censusSampleXlsxUrl() {
    return `${API}/employer/census/sample.xlsx`;
}


/* ---------- Phase 3a — Admin underwriting outcomes ---------- */
export async function adminUnderwritingOutcomesRead(employerId) {
    const res = await http.get(
        `/admin/employer-aggregate/${employerId}/underwriting-outcomes`,
    );
    return res.data;
}

export async function adminSetCoverageApproved(employerId, valueCents, notes = "") {
    const res = await http.post(
        `/admin/employer-aggregate/${employerId}/coverage-approved`,
        { value_cents: valueCents, notes },
    );
    return res.data;
}

export async function adminSetCoverageEffectiveDate(employerId, value, notes = "") {
    const res = await http.post(
        `/admin/employer-aggregate/${employerId}/coverage-effective-date`,
        { value, notes },
    );
    return res.data;
}


/* ---------- Phase A5 — Admin-initiated password reset + audit ---------- */
export async function adminUsersSearch(q) {
    const res = await http.get("/admin/users/search", { params: { q } });
    return res.data;
}

export async function adminUserDetail(role, userId) {
    const res = await http.get(`/admin/users/${role}/${userId}`);
    return res.data;
}

export async function adminSendReset(role, userId) {
    const res = await http.post(
        `/admin/users/${role}/${userId}/password/send-reset`, {},
    );
    return res.data;
}

export async function adminGenerateTemp(role, userId, justification) {
    const res = await http.post(
        `/admin/users/${role}/${userId}/password/generate-temp`,
        { justification },
    );
    return res.data;
}
